import React, { Fragment, useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import { CopyToClipboardButton } from '../base/components/Misc';
import PropControl from '../base/components/PropControl';
import ServerIO from '../plumbing/ServerIO';


/** List available fonts already on the server */
function FontsList({list, selectedFont, setFont, setSubset, subset, subsets}) {
	if (!list) return <Card body>Retrieving font list...</Card>;
	if (!list.length) return <Card body>There are no fonts in this system yet. Use the widget above to upload what you need.</Card>;

	return <Card className="mb-2">
		<CardHeader>
			Previously uploaded fonts
		</CardHeader>
		<CardBody>
			<p>The fonts listed here are available for immediate use. Select one to generate a CSS block to insert in an adunit.</p>
			{list.map(font => (
				<>
				<Button color={font === selectedFont ? 'primary' : 'default'} type="button" key={font} className="d-block mb-1" onClick={() => {
					setSubset(null);
					setFont(font);	
				}}>
					{font}
				</Button>
				{font === selectedFont && <SubsetsList font={font} selectedSubset={subset} subsets={subsets} setSubset={setSubset} />}
				{font == selectedFont && <CssOutput font={font} subsetName={subset} subsetUrls={subsets && subsets[subset]} />}
				</>
			))}
		</CardBody>
	</Card>;
}


function SubsetsList({font, selectedSubset, subsets, setSubset}) {
	if (!font) return null;
	if (!subsets) {
		return <Card className="mb-2" body>Retrieving subsets for <code>{font}</code>...</Card>;
	}
	if (!Object.keys(subsets).length) {
		return <Card className="mb-2" body>No subsets for <code>{font}</code>? That shouldn't happen - email roscoe@good-loop.com and copy-paste the full name of the font you were trying to use.</Card>;
	}

	// raw version might be any format so we need to just grab the first
	const rawFont = Object.values(subsets.raw).find(url => !!url);
	
	// Generate a sampler for the selected font - and selected subset, to allow checking character support against required strings
	let sampler = null;
	let samplerFont = selectedSubset ? subsets[selectedSubset] : subsets.all;
	if (samplerFont) {
		sampler = <Card className="mb-2" body>
			<style>{`@font-face { font-family: "Selected-Font-Preview"; src: url("${samplerFont.woff}") format("woff"); }`}</style>
			<p className="mb-0" style={{fontFamily: 'Selected-Font-Preview', fontSize: '200%'}} contentEditable suppressContentEditableWarning>
				The quick brown fox jumps over the lazy dog.
			</p>
			<p className="mb-0">
				{selectedSubset ? `Previewing subset "${selectedSubset}". ` : ''}
				This sampler can be edited.
			</p>
		</Card>;
	}

	return <Card className="mb-2">
		<CardHeader >
			{font} Subsets
		</CardHeader>
		<CardBody >
			<p>Original font file: <a href={rawFont}>{rawFont}</a></p>
			{sampler}
			<p>Select one of these language subsets to generate your CSS:</p>
			{Object.entries(subsets).filter(([subset]) => subset !== 'raw').sort(([a], [b]) => a.localeCompare(b)).map(([subset]) => (
				<Button color={subset === selectedSubset ? 'primary' : 'default'} type="button" key={subset} className="mr-1" onClick={() => setSubset(subset)}>
					{subset}
				</Button>
			))}
			
		</CardBody>
	</Card>;
}


function CssOutput({font, subsetName, subsetUrls}) {
	if (!font || !subsetName || !subsetUrls) return null;

	const fontTrim = font.replace(/-\d+$/, '');

	const css = `
/* Define the font-face */
@font-face {
	font-family: "${fontTrim}";
	src: url("${subsetUrls.woff}") format("woff"),
		url("${subsetUrls.woff2}") format("woff2");
}
/* Basic usage: Apply the font to the video player and endcard */
#player, #end-card {
	font-family: "${fontTrim}";
}`;


	return <Card className="mb-2">
		<CardHeader>Generated CSS</CardHeader>
		<CardBody>
			<p>CSS snippet for uploaded font <code>{font}</code>, subset <code>{subsetName}</code></p>
			<CopyToClipboardButton color="primary" className="d-block" text={css} />
			<pre>{css}</pre>
		</CardBody>
	</Card>;
}


function FontsPage({}) {
	const [fonts, setFonts] = useState(null); // Available fonts according to server
	const [subsets, setSubsets] = useState(null); // Map of subset names to URLs for selected font
	const [font, setFont] = useState(null); // Current selected font
	const [subset, setSubset] = useState(null); // Current selected subset
	const [uploadNonce, setUploadNonce] = useState(null);

	// Populate the fonts list
	useEffect(() => {
		fetch(`${ServerIO.MEDIA_ENDPOINT}/fonts`)
		.then(res => res.json())
		.then(json => {
			if (!json.data || !json.data.fonts) setFonts([]);
			setFonts(json.data.fonts);
		});
	}, [uploadNonce]); // Uploading invalidates this list

	// Populate the subsets list when a base font is chosen
	useEffect(() => {
		setSubsets(null);
		if (!font) return;
		fetch(`${ServerIO.MEDIA_ENDPOINT}/fonts/${font}`)
		.then(res => res.json())
		.then(json => {
			if (!json.data || !json.data.subsets) setSubsets([]);
			setSubsets(json.data.subsets);
		});
	}, [font]);

	// font filter, just a basic fontName.includes(filter) check
	const [filter, setFilter] = useState("");

	// if the user is searching for a font, filter to only font names that have substring of filter
	let filteredFonts = filter ? fonts.filter(fontName => fontName.toLowerCase().includes(filter)) : fonts;

	// taking resulting list, sort list to be case sensitive alphabetically (Aa Bb Cc ...)
	if(filteredFonts) {
	filteredFonts.sort((a, b) => {
		a = a[0];
		b = b[0];
		let aa = a.toLowerCase(), bb = b.toLowerCase();
		return aa.localeCompare(bb) || (a === aa) - (b === bb);
		});
	}
	
	return <>
		<h1>FONTS!</h1>
		<p>This page has some tools for uploading, processing and managing fonts for use in Good-Loop ads.</p>
		<Card className="mb-2">
			<CardHeader>Upload</CardHeader>
			<CardBody>
				<PropControl type="fontUpload" path={['widget', 'FontUploadTest']} prop="font" label="Upload a new font:" onUpload={() => setUploadNonce(btoa(Math.random()))} />
				<input label="Font search" id="fontFilter" name="fontFilter" type="filter" onChange={(e) => setFilter(e.target.value.toLowerCase())} value={filter}/>
			</CardBody>
		</Card>
		<FontsList list={filteredFonts} selectedFont={font} subset={subset} subsets={subsets} setFont={setFont} setSubset={setSubset} />
		<SubsetsList font={font} selectedSubset={subset} subsets={subsets} setSubset={setSubset} />
		<CssOutput font={font} subsetName={subset} subsetUrls={subsets && subsets[subset]} />
	</>;
}

export default FontsPage;
